// src/components/nauphilus/NauphilusReportList.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import Pagination from './Pagination';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { fetchExcelReport, fetchInterviews, fetchCustomers } from './api/nauphilusApi';
import { Modal, ProgressBar } from 'react-bootstrap';

interface Customer {
  id: number;
  name: string;
}

const NauphilusReportList: React.FC = () => {
  const intl = useIntl();

  const [data, setData] = useState<any[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // Filtros
  const [searchValue, setSearchValue] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [createdAtDateInit, setCreatedAtDateInit] = useState('');
  const [createdAtDateEnd, setCreatedAtDateEnd] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    searchValue: '',
    searchCustomer: '',
    createdAtDateInit: '',
    createdAtDateEnd: ''
  });

  const [orderType, setOrderType] = useState('fechaRegistro');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  const [timezone, setTimezone] = useState('');

  // Modal detalle
  const [selected, setSelected] = useState<any | null>(null);

  const memoizedData = useMemo(() => data, [data]);

  // ===== Helpers =====

  const normalizePercentage = (value: any): number | null => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    const percentage = numericValue <= 1 ? numericValue * 100 : numericValue;
    return Math.max(0, Math.min(percentage, 100));
  };

  const getProviderResponse = (row: any): any => {
    return row?.providerResponse?.providerResponse ?? row?.providerResponse;
  };

  const getInterviewCode = (row: any): number | null => {
    const providerResponse = getProviderResponse(row);
    const code = Number(providerResponse?.widgetEvent?.code);
    return Number.isFinite(code) ? code : null;
  };

  const isInterviewCompleted = (row: any): boolean => {
    return row?.estadoEntrevista === 'Completada' || getInterviewCode(row) === 2201;
  };

  const getInterviewProgress = (row: any): number => {
    const providerResponse = getProviderResponse(row);
    const progress = normalizePercentage(
      row?.porcentajeEntrevista ??
      providerResponse?.widgetEvent?.detail?.percentage
    );

    if (progress !== null) return progress;
    return isInterviewCompleted(row) ? 100 : 0;
  };

  const hasProviderResponse = (row: any): boolean => {
    return !!row?.providerResponse && Object.keys(row.providerResponse).length > 0;
  };

  const getInterviewProgressInfo = (percentage: number) => {
    if (percentage >= 100) return { color: "#00C851", text: "Entrevista completada" };
    return { color: "#0d6efd", text: "Avance de entrevista" };
  };

  const getProgressBarData = (row: any) => {
    if (isInterviewCompleted(row)) {
      return {
        value: 100,
        color: '#00C851',
        text: 'Entrevista completada',
      };
    }

    const providerResponse = getProviderResponse(row);
    const progressPercentage =
      row?.porcentajeEntrevista ??
      providerResponse?.progressPercentage ??
      providerResponse?.widgetEvent?.detail?.percentage;
    const normalized = normalizePercentage(progressPercentage);

    if (normalized !== null) {
      return {
        value: normalized,
        color: '#0d6efd',
        text: 'Avance de entrevista',
      };
    }

    return null;
  };

  const getProgressInfo = (stars: number, appKey?: string) => {
    let value = stars ?? 0;
    if (value < 0) value = 0;
    if (value > 100) value = 100;

    let color = '#999999';
    let text = 'Sin resultado';

    if (appKey === 'CREDEQUIP' || appKey === 'ENTRETAIL') {
      if (value <= 25) {
        color = '#C62828';
        text = 'Muy baja probabilidad de pago';
      } else if (value <= 50) {
        color = '#EF6C00';
        text = 'Poca probabilidad de pago';
      } else if (value <= 75) {
        color = '#9E7D00';
        text = 'Moderada probabilidad de pago';
      } else {
        color = '#2E7D32';
        text = 'Alta probabilidad de pago';
      }
    } else if (appKey === 'FRAUDCEL') {
      if (value >= 76) {
        color = '#C62828';
        text = 'Muy baja probabilidad de pago';
      } else if (value >= 51) {
        color = '#EF6C00';
        text = 'Poca probabilidad de pago';
      } else if (value >= 26) {
        color = '#9E7D00';
        text = 'Moderada probabilidad de pago';
      } else {
        color = '#2E7D32';
        text = 'Alta probabilidad de pago';
      }
    }

    return { color, text, value };
  };

  const getScoreSummary = (row: any) => {
    const providerResponse = getProviderResponse(row);
    const finalStars =
      providerResponse?.stars ??
      providerResponse?.widgetEvent?.detail?.evalResult?.stars;

    if (finalStars === undefined || finalStars === null) return null;

    const score = Number(finalStars);
    if (!Number.isFinite(score)) return null;

    const { color, text, value } = getProgressInfo(score, row.appKey);
    return { value, color, text };
  };

  const compactVisibleFields = (data: Record<string, any> = {}) =>
    Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );

  const removeDuplicatedFields = (
    source: Record<string, any> = {},
    baseline: Record<string, any> = {}
  ) =>
    Object.fromEntries(
      Object.entries(source).filter(([key, value]) => {
        if (value === undefined || value === null || value === '') return false;

        const relatedKeys = [key];
        if (key === 'firstName') relatedKeys.push('name', 'nombres');
        if (key === 'lastName') relatedKeys.push('lastname1', 'apellidoPaterno');
        if (key === 'secondLastName') relatedKeys.push('lastname2', 'apellidoMaterno');

        return !relatedKeys.some((candidate) => baseline[candidate] === value);
      })
    );

  const detailSections = (row: any) => {
    const baseDetail = compactVisibleFields({
      idHub: row.idHub || '',
      nombres: row.nombres || '',
      apellidoPaterno: row.apellidoPaterno || '',
      apellidoMaterno: row.apellidoMaterno || '',
      appKey: row.appKey || '',
      estadoEntrevista: row.estadoEntrevista || '',
      fechaRegistro: row.fechaRegistro || '',
      horaRegistro: row.horaRegistro || '',
      customerName: row.customerName || '',
    });

    const prospectDetail = compactVisibleFields(row.prospect || {});
    const capturedAnswers = removeDuplicatedFields(
      compactVisibleFields(row.prospectRiskVars || {}),
      {
        ...baseDetail,
        ...prospectDetail,
      }
    );

    const providerDetail = compactVisibleFields({ ...(getProviderResponse(row) || {}) });

    if (providerDetail.progressPercentage !== undefined) {
      delete providerDetail.progressPercentage;
    }

    if (providerDetail.prospectRiskVars !== undefined) {
      delete providerDetail.prospectRiskVars;
    }

    if (providerDetail.widgetEvent?.detail) {
      const sanitizedWidgetEvent = {
        ...providerDetail.widgetEvent,
        detail: { ...providerDetail.widgetEvent.detail },
      };

      if (!isInterviewCompleted(row)) {
        delete sanitizedWidgetEvent.detail.percentage;
      }

      if (sanitizedWidgetEvent.detail.appKey === row.appKey) {
        delete sanitizedWidgetEvent.detail.appKey;
      }

      if (sanitizedWidgetEvent.detail.productKey === row.appKey) {
        delete sanitizedWidgetEvent.detail.productKey;
      }

      if (Object.keys(sanitizedWidgetEvent.detail).length === 0) {
        delete sanitizedWidgetEvent.detail;
      }

      if (Object.keys(sanitizedWidgetEvent).length === 0) {
        delete providerDetail.widgetEvent;
      } else {
        providerDetail.widgetEvent = sanitizedWidgetEvent;
      }
    }

    const sections: Array<{ title: string; value: Record<string, any> }> = [
      { title: 'Resumen', value: baseDetail },
    ];

    if (Object.keys(prospectDetail).length > 0) {
      sections.push({ title: 'Prospecto', value: prospectDetail });
    }

    if (Object.keys(capturedAnswers).length > 0) {
      sections.push({ title: 'Respuestas capturadas', value: capturedAnswers });
    }

    if (Object.keys(providerDetail).length > 0) {
      sections.push({ title: 'Resultado', value: providerDetail });
    }

    return sections;
  };

  const renderRow = (key: string, value: any): JSX.Element => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return (
        <div className="mb-3" key={key} style={{ width: '100%' }}>
          <h5 className="fw-bold">    {intl.formatMessage({ id: key })}</h5>
          <div className="row">
            {Object.entries(value).map(([k, v]) => (
              <React.Fragment key={k}>{renderRow(k, v)}</React.Fragment>
            ))}
          </div>
        </div>
      );
    }
    if (Array.isArray(value)) {
      return (
        <div className="row mb-2" key={key}>
          {value.map((v, i) => renderRow(`${key}[${i}]`, v))}
        </div>
      );
    }
    return (
      <div className="col-md-4 mb-2" key={key} style={{ wordBreak: 'break-word' }}>
        <label className="form-label fw-bold">{intl.formatMessage({ id: key })}</label>
        <div>{typeof value === 'boolean' ? (value ? 'Sí' : 'No') : value ?? ''}</div>
      </div>
    );
  };

  // ===== API Calls =====
  const fetchCustomerList = useCallback(async () => {
    try {
      const customersResponse = await fetchCustomers();
      setCustomers(customersResponse.customers || customersResponse);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { rows, totalPages, timezone } = await fetchInterviews(
        currentPage,
        limit,
        appliedFilters.searchValue,
        appliedFilters.searchCustomer,
        appliedFilters.createdAtDateInit,
        appliedFilters.createdAtDateEnd,
        orderType,
        orderDirection
      );
      setData(rows);
      setTotalPages(totalPages);
      setTimezone(timezone);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, limit, appliedFilters, orderType, orderDirection]);

  useEffect(() => { fetchCustomerList(); }, [fetchCustomerList]);
  useEffect(() => { loadData(); }, [loadData]);

  // ===== Filtros =====
  const handleSearch = () => {
    setAppliedFilters({
      searchValue,
      searchCustomer,
      createdAtDateInit,
      createdAtDateEnd
    });
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchValue('');
    setSearchCustomer('');
    setCreatedAtDateInit('');
    setCreatedAtDateEnd('');
    setAppliedFilters({ searchValue: '', searchCustomer: '', createdAtDateInit: '', createdAtDateEnd: '' });
    setCurrentPage(1);
  };

  // ===== Descargar CSV =====
  const handleDownload = async () => {
    try {
      Swal.fire({
        html: `<div class="spinner-container"><i class="fas fa-spinner fa-spin"></i> Generando reporte...</div>`,
        showConfirmButton: false,
        allowOutsideClick: false,
      } as SweetAlertOptions);

      const signedUrl = await fetchExcelReport(
        appliedFilters.searchValue,
        appliedFilters.searchCustomer,
        appliedFilters.createdAtDateInit,
        appliedFilters.createdAtDateEnd,
        orderType,
        orderDirection
      );

      if (signedUrl) {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = 'nauphilus_report.csv';
        link.click();
      } else {
        console.error('No se pudo generar la URL firmada del archivo.');
      }

      Swal.close();
    } catch (err) {
      Swal.close();
      console.error('Error al generar el reporte:', err);
    }
  };

  // ===== Render =====
  if (isLoading) return <div>Cargando...</div>;
  if (error) return <div>Error al cargar los datos</div>;

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-body py-3">

        {/* Filtros */}
        <div className="row flex-wrap mb-5">
          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <label>Buscar</label>
            <input
              type="text"
              className="form-control form-control-solid form-control-lg"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <label>Socio comercial</label>
            <select
              className="form-select form-select-solid form-control-lg"
              value={searchCustomer}
              onChange={(e) => setSearchCustomer(e.target.value)}
            >
              <option value="">Selecciona un socio comercial</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <label>Fecha inicio</label>
            <input
              type="date"
              className="form-control form-control-solid form-control-lg"
              value={createdAtDateInit}
              onChange={(e) => setCreatedAtDateInit(e.target.value)}
            />
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <label>Fecha fin</label>
            <input
              type="date"
              className="form-control form-control-solid form-control-lg"
              value={createdAtDateEnd}
              onChange={(e) => setCreatedAtDateEnd(e.target.value)}
            />
          </div>
        </div>

        {/* Botones */}
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary me-2" onClick={handleSearch}>Buscar</button>
          <button className="btn btn-secondary me-2" onClick={handleReset}>Borrar</button>
          <button className="btn btn-primary" onClick={handleDownload}>Descargar reporte</button>
        </div>

        {/* Tabla */}
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>#</th>
                <th>Socio comercial</th>
                <th>ID Global</th>
                <th>ID Hub</th>
                <th>RFC</th>
                <th>CURP</th>
                <th>Nombres</th>
                <th>Apellido Paterno</th>
                <th>Apellido Materno</th>
                <th>Estado</th>
                <th>Fecha registro</th>
                <th>Hora registro</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {memoizedData.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center">Sin resultados</td>
                </tr>
              ) : (
                memoizedData.map((r, idx) => {
                  const progress = getInterviewProgress(r);
                  const isCompleted = isInterviewCompleted(r) || progress >= 100;
                  const canViewDetail = hasProviderResponse(r);
                  const statusLabel = isCompleted ? 'Completada' : 'Pendiente';

                  return (
                    <tr key={r.idGlobal || idx}>
                      <td>{(currentPage - 1) * limit + idx + 1}</td>
                      <td>{r.customerName}</td>
                      <td>{r.idGlobal}</td>
                      <td>{r.idHub}</td>
                      <td>{r.rfc || r.prospectRiskVars?.rfc || r.prospect?.rfc || '-'}</td>
                      <td>{r.curp || r.prospectRiskVars?.curp || r.prospect?.curp || '-'}</td>
                      <td>{r.nombres}</td>
                      <td>{r.apellidoPaterno}</td>
                      <td>{r.apellidoMaterno}</td>
                      <td>{statusLabel}</td>
                      <td>{r.fechaRegistro}</td>
                      <td>{r.horaRegistro}</td>
                      <td>
                        {/* Botón Ver detalle */}
                        <button
                          className="btn btn-primary btn-sm me-1"
                          onClick={canViewDetail ? () => setSelected(r) : undefined}
                          disabled={!canViewDetail}
                          style={{
                            backgroundColor: 'var(--kt-primary)',
                            borderColor: 'var(--kt-primary)',
                            color: 'white',
                            pointerEvents: canViewDetail ? 'auto' : 'none',
                            opacity: 1,
                          }}
                          title={canViewDetail ? 'Ver detalle' : 'Detalle no disponible'}
                          aria-label={`Detalle de entrevista ${r.idGlobal}`}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="d-flex justify-content-between mt-3">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="form-select form-select-sm"
            style={{ width: '80px' }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          {memoizedData.length > 0 && (
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              limit={limit}
              lengthData={memoizedData.length}
            />
          )}
        </div>

        {/* Modal detalle */}
        {selected && (
          <Modal show={!!selected} onHide={() => setSelected(null)} size="xl" centered>
            <Modal.Header closeButton>
              <Modal.Title>
                Entrevista #{selected.idGlobal} – {selected.appKey || 'N/A'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' }}>
              {selected.providerResponse ? (
                <>
                  {/* Barra de progreso */}
                  {(() => {
                    const progressData = getProgressBarData(selected);
                    if (!progressData) return null;

                    const { value, color, text } = progressData;
                    return (
                      <div className="mb-3">
                        <ProgressBar
                          now={value}
                          label={`${value.toFixed(2)}%`}
                          style={{
                            backgroundColor: "#e0e0e0",
                            height: "25px",
                            fontWeight: "bold",
                          }}
                        >
                          <div
                            style={{
                              width: `${value}%`,
                              backgroundColor: color,
                              height: "100%",
                              borderRadius: "0.25rem",
                              transition: "width 0.6s ease",
                              textAlign: "center",
                              color: "#000",
                              fontWeight: "bold",
                            }}
                          >
                            {`${value.toFixed(2)}%`}
                          </div>
                        </ProgressBar>
                        <div className="mt-2 text-center fw-semibold">{text}</div>
                      </div>
                    );
                  })()}

                  {getScoreSummary(selected) && isInterviewCompleted(selected) && (() => {
                    const scoreData = getScoreSummary(selected);
                    if (!scoreData) return null;

                    return (
                      <div className="mb-3 text-center">
                        <div className="fw-semibold">Score final: {scoreData.value}</div>
                        <div style={{ color: scoreData.color }} className="fw-semibold">
                          {scoreData.text}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Detalle jerárquico */}
                  <div className="container-fluid">
                    {detailSections(selected).map((section) => (
                      <div key={section.title} className="mb-3">
                        <h5 className="fw-semibold mt-3 mb-2">
                          {intl.formatMessage({ id: section.title })}
                        </h5>
                        <div className="row">
                          {Object.entries(section.value).map(([k, v]) => (
                            <React.Fragment key={`${section.title}-${k}`}>
                              {renderRow(k, v)}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p>No hay datos para mostrar</p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <button className="btn btn-secondary" onClick={() => setSelected(null)}>Cerrar</button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default NauphilusReportList;
